<script setup lang="ts">
useSeo({
  title: `Poker | ${appName()} | Cassino Online | Casa de Apostas`,
});

const { t } = useTexts();
const { updateWallet } = useWallet();

const { isAuthenticated } = storeToRefs(useAuthStore());
const { isGameModalOpen } = storeToRefs(useCasinoStore());
const { menuOpen } = storeToRefs(useLayoutStore());
const { toggleMenuOpen } = useLayoutStore();
const { push } = useRouter();

const {
  headerHeight,
  topBarHeight,
  horizontalMenuHeight,
  isMobile,
  isLayoutMobileOrTablet,
} = useLayout();

const pokerPageHeight = computed<string>(() => {
  const sumHeight =
    headerHeight.value + topBarHeight.value + horizontalMenuHeight.value;

  return `calc(100dvh - ${sumHeight}px)`;
});

const gameSlug = computed<string>(() => {
  return `evenbetgaming/poker`;
});

const activeGame = ref<any>({
  id: 41675,
  url: '',
  name: 'Poker',
  provider: 'Evenbet Gaming',
  provider_id: 325,
  type: null,
  image:
    'https://imagedelivery.net/BgH9d8bzsn4n0yijn4h7IQ/e91ae062-3793-4988-cf6a-b3512579eb00',
  error: false,
  error_login: false,
  error_msg: '',
  demo: 0,
  rtp: 0,
});

const isGameLoading = ref(true);

const fullScreenOn = () => {
  isGameModalOpen.value = true;
};

const fullScreenOffAndReturnToPoker = () => {
  push('/poker');
  updateWallet();
  isGameModalOpen.value = false;
};

const getIframe = async () => {
  const platform = window.innerWidth < 768 ? 'MOBILE' : 'WEB';

  if (gameSlug.value && activeGame.value.provider_id) {
    if (!isAuthenticated.value) {
      activeGame.value.url = '';
      activeGame.value.error = true;
      activeGame.value.error_login = true;
      activeGame.value.error_msg = t('casino.game.login_error');

      return;
    }

    const { ApiService } = useApiService();

    await ApiService.get(
      `/start-game-v2?slug=${gameSlug.value}&platform=${platform}`,
    )
      .then((res) => {
        if (!isAuthenticated.value && isMobile.value) {
          return;
        }

        activeGame.value.url = res.data.gameURL;
        isGameLoading.value = false;
      })
      .catch((err) => {
        if (err) {
          isGameLoading.value = false;
          activeGame.value.url = '';
          activeGame.value.error = true;
          if (err.message && err.message.includes('401')) {
            activeGame.value.error_login = true;
            activeGame.value.error_msg = t('casino.game.login_error');
          } else {
            activeGame.value.error_login = false;
            activeGame.value.error_msg = err.message
              ? err.message
              : t(`error.game_not_found`);
          }
        }
      });
  }
};

watch(
  () => isAuthenticated.value,
  () => {
    if (isAuthenticated.value) {
      if (activeGame.value.error_login) {
        activeGame.value.error = false;
        activeGame.value.error_login = false;
        activeGame.value.error_msg = '';
      }
      getIframe();
    }
  },
);

onBeforeMount(() => {
  document.body.style.removeProperty('overflow');
});

onMounted(() => {
  if (!isAuthenticated.value) {
    fullScreenOffAndReturnToPoker();

    return;
  }

  if (isMobile.value) {
    fullScreenOn();
  }

  if (gameSlug.value) {
    getIframe();
  }

  if (menuOpen.value) {
    toggleMenuOpen();
  }
});

onBeforeRouteLeave(() => {
  if (!menuOpen.value && !isLayoutMobileOrTablet.value) {
    toggleMenuOpen();
  }
});
</script>

<template>
  <section :class="$style.pokerPage">
    <CasinoGameIframe
      v-if="!isMobile || (isMobile && isGameModalOpen)"
      :active-game="activeGame"
      :class="$style.pokerDesktopIframe"
      :game-demo-mode="'real'"
      :game-slug="gameSlug"
      :is-game-loading="isGameLoading"
      :is-game-modal-open="isGameModalOpen"
      :is-soft-swiss-game="false"
      @full-screen-off="fullScreenOffAndReturnToPoker()"
    />
  </section>
</template>

<style module lang="postcss">
.pokerPage {
  @apply min-w-full flex justify-center items-center relative;

  min-height: v-bind(pokerPageHeight);

  .pokerDesktopIframe {
    @apply border-none m-0 h-full w-full max-w-full;

    min-height: v-bind(pokerPageHeight);
  }
}

.loading {
  @apply h-full absolute flex items-center z-10;
}
</style>
